import { Capacitor } from '@capacitor/core';

const env = process.env;

/** True inside the Capacitor (Android/iOS) app, false in a browser. */
export const isNative = (): boolean => Capacitor.isNativePlatform();

/** True in an Android debug build (assembleDebug): MainActivity exposes it as window.AndroidBuild. */
const isDebugBuild = (): boolean => {
    try {
        return Boolean((window as any).AndroidBuild?.isDebug());
    } catch {
        return false;
    }
};

export const config = {
    /** Developer options (skip to the last 10 seconds). Always on in development; opt in for a build with REACT_APP_DEV_TOOLS=true. */
    devTools:
        env.NODE_ENV !== 'production' || env.REACT_APP_DEV_TOOLS === 'true',

    /** AdSense publisher id, e.g. ca-pub-1234567890123456. Unset = no web ads. */
    adsenseClient: env.REACT_APP_ADSENSE_CLIENT || '',
    adsenseFooterSlot: env.REACT_APP_ADSENSE_FOOTER_SLOT || '',
    adsenseBreakSlot: env.REACT_APP_ADSENSE_BREAK_SLOT || '',
    /** Show placeholder ads when no ad network is configured (development only). */
    adsPreview: env.REACT_APP_ADS_PREVIEW === 'true',
    /** Ask AdSense for test ads (data-adtest). Lets real units be seen on localhost without risking invalid clicks. Never on in production. */
    adsenseTest: env.REACT_APP_ADSENSE_TEST === 'true',

    // tomatopop-break (interstitial) and tomatopop-footer (banner). Test ads show unless a production build says otherwise.
    admobInterstitialId:
        env.REACT_APP_ADMOB_INTERSTITIAL_ID ||
        'ca-app-pub-8117664763681865/9085387132',
    admobBannerId:
        env.REACT_APP_ADMOB_BANNER_ID ||
        'ca-app-pub-8117664763681865/1757561457',
    // Rewarded video for the streak gift. Until a real unit exists, a production build has none (the gift is then free);
    // development and debug builds use Google's sample rewarded unit.
    admobRewardedId:
        env.REACT_APP_ADMOB_REWARDED_ID ||
        (isDebugBuild() || env.NODE_ENV !== 'production'
            ? 'ca-app-pub-3940256099942544/5224354917'
            : ''),
    admobTesting:
        isDebugBuild() ||
        (env.REACT_APP_ADMOB_TESTING !== undefined &&
        env.REACT_APP_ADMOB_TESTING !== ''
            ? env.REACT_APP_ADMOB_TESTING !== 'false'
            : env.NODE_ENV !== 'production'),

    /** Where web visitors can chip in (Ko-fi, Buy Me a Coffee, GitHub Sponsors...). Unset = the button explains it is not set up. */
    donateUrl: env.REACT_APP_DONATE_URL || 'https://ko-fi.com/gagoworks',
    /** Brazilian Pix donations (a Mercado Pago payment page). Optional; shown next to the tip link. Defaults to the public Mercado Pago link. */
    pixUrl:
        env.REACT_APP_PIX_URL || 'https://link.mercadopago.com.br/gagoworks',

    lifetimeProductId:
        env.REACT_APP_LIFETIME_PRODUCT_ID || 'works.gago.tomatopop.lifetime',
    lifetimeFallbackPrice: '$3.00',

    /** Store listings for the "Get on mobile" dialog. A badge is "coming soon" until its listing is live. */
    androidPackage: env.REACT_APP_ANDROID_PACKAGE || 'works.gago.tomatopop',
    androidLive: env.REACT_APP_ANDROID_LIVE === 'true',
    iosAppId: env.REACT_APP_IOS_APP_ID || '',

    supportEmail: env.REACT_APP_SUPPORT_EMAIL || 'support.tomatopop@gago.works',
};

/**
 * A web ad is only drawn when its own AdSense unit exists (publisher id AND slot id), or in the
 * development preview. A publisher id alone would draw an empty bar and an empty ad break.
 */
export const webFooterAdConfigured = (): boolean =>
    Boolean(config.adsenseClient && config.adsenseFooterSlot) ||
    config.adsPreview;

export const webBreakAdConfigured = (): boolean =>
    Boolean(config.adsenseClient && config.adsenseBreakSlot) ||
    config.adsPreview;
